/**
 * Fuzzy matching for search boxes (Jacob, 2026-09-24: typing "mke" has to find
 * mikehammermoving.com).
 *
 * One helper for every search box so they all behave the same:
 *   - case, spaces and punctuation don't matter, and URLs are compared without
 *     "https://", "www." or a trailing slash;
 *   - a query matches when it is the whole text, its start, the start of a
 *     word (running on across the following words), or anywhere inside one
 *     word; or, outside strict mode, when its letters
 *     appear in order inside ONE word of the text ("mke" in
 *     "mikehammermoving"), or within a small typo of a word or of the start
 *     of the text (1 edit for 4 to 7 characters, 2 for 8 or more);
 *   - every match has a score, so results sort best first:
 *     exact > prefix > word start > substring > letters-in-order > typo.
 *
 * ⛔ THREE RULES THAT KEEP IT FROM PULLING IN UNRELATED ROWS (reviewer-2 on
 * #2996: on Keyword Review, "near me" matched 141 rows where 6 contain it,
 * and those rows sit under a bulk Pause that writes to Google Ads):
 *   1. Every field is scored on its own: callers pass [keyword, ad group,
 *      campaign], never one joined string, so a match cannot straddle fields.
 *   2. Letters-in-order only counts inside a single word of a single field,
 *      and inside a tight window of that word.
 *   3. Monotonic by construction: a text matches a query only if it also
 *      matches every shorter start of that query. Typing one more letter can
 *      only narrow the list, never widen it.
 * Plus: all-digit queries match as substrings only, a query with a digit is
 * never typo-matched ("market-7" is not "Market-1"), and `strict` (for tables
 * whose selection feeds a write) allows exact / prefix / word / substring only.
 *
 * No dependency: the texts are short (client names, domains, keywords), so a
 * plain edit-distance loop is fast enough for lists of a few thousand rows.
 */

export type FuzzyMatchKind =
  | 'exact'
  | 'prefix'
  | 'word'
  | 'substring'
  | 'subsequence'
  | 'typo';

/** Score bands. Higher is better; 0 means no match. */
export const FUZZY_SCORE: Record<FuzzyMatchKind, number> = {
  exact: 100,
  prefix: 90,
  word: 80,
  substring: 70,
  subsequence: 50,
  typo: 30,
};

/** From this length a query may match with a typo. */
export const MIN_TYPO_QUERY_LENGTH = 4;

export interface FuzzyOptions {
  /** Exact, prefix, word start and substring only: no letters-in-order and no
   *  typos. For tables whose selection feeds a write (bulk Pause / Enable /
   *  Apply), where a loose match could put an unrelated row under the action. */
  strict?: boolean;
}

/** Edits allowed for a query of this (compact) length. */
export function allowedTypos(length: number): number {
  if (length < MIN_TYPO_QUERY_LENGTH) return 0;
  return length >= 8 ? 2 : 1;
}

/** Widest stretch of one word that letters-in-order may spread over. */
export function maxSubsequenceSpan(queryLength: number): number {
  return Math.max(queryLength * 3, queryLength + 6);
}

interface Normalized {
  /** Words, lowercased, punctuation removed. */
  words: string[];
  /** Everything run together, e.g. "mikehammermovingcom". */
  compact: string;
}

/** Lowercase, drop the URL scheme, "www." and a trailing slash, strip
 *  accents, and split on anything that is not a letter or digit. */
export function normalizeForSearch(raw: string | null | undefined): Normalized {
  let s = (raw ?? '').toLowerCase().trim();
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  s = s.replace(/^www\./, '');
  s = s.replace(/\/+$/, '');
  s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  const words = s.split(/[^a-z0-9]+/).filter(Boolean);
  return { words, compact: words.join('') };
}

/** The tightest span of `hay` holding `needle` as a subsequence, or 0. */
function tightestSpan(needle: string, hay: string): number {
  let best = 0;
  for (let s = 0; s < hay.length; s++) {
    if (hay[s] !== needle[0]) continue;
    let i = 1;
    let j = s + 1;
    for (; j < hay.length && i < needle.length; j++) {
      if (hay[j] === needle[i]) i++;
    }
    if (i < needle.length) break; // no later start can finish either
    const span = j - s;
    if (!best || span < best) best = span;
    if (best === needle.length) break;
  }
  return best;
}

/** Levenshtein distance between `q` and the closest PREFIX of `t` (or, with
 *  `anywhere`, the closest substring of `t`), stopping once over `limit`. */
function boundedDistance(q: string, t: string, limit: number, anywhere: boolean): number {
  const m = q.length;
  const n = t.length;
  if (m === 0) return 0;
  let prev = new Array<number>(n + 1);
  let cur = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = anywhere ? 0 : j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= n; j++) {
      const cost = q[i - 1] === t[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > limit) return limit + 1;
    [prev, cur] = [cur, prev];
  }
  let best = m;
  for (let j = 0; j <= n; j++) if (prev[j] < best) best = prev[j];
  return best;
}

/** One compact query against one normalized field, ignoring the prefix chain. */
function scoreNormalized(qc: string, t: Normalized, strict: boolean): number {
  const tc = t.compact;
  if (!qc || !tc) return 0;
  if (tc === qc) return FUZZY_SCORE.exact;
  if (tc.startsWith(qc)) return FUZZY_SCORE.prefix;
  for (let w = 0; w < t.words.length; w++) {
    if (t.words.slice(w).join('').startsWith(qc)) return FUZZY_SCORE.word;
  }
  // Mid-word matches stay inside ONE word: "em" is in "emergency" and
  // "hemet", but not in "near me" by way of the run-together "nearme".
  // Anything spanning words has to start at a word (the check above).
  if (t.words.some((w) => w.includes(qc))) return FUZZY_SCORE.substring;
  if (strict) return 0;
  // All-digit queries (ZIP codes, ids) match as substrings only.
  if (/^\d+$/.test(qc)) return 0;

  // Letters in order, inside ONE word, inside a tight stretch of it.
  const window = maxSubsequenceSpan(qc.length);
  let bestSpan = 0;
  let startsWord = false;
  for (const word of t.words) {
    if (word.length < qc.length) continue;
    const span = tightestSpan(qc, word);
    if (span && span <= window && (!bestSpan || span < bestSpan)) {
      bestSpan = span;
      startsWord = word[0] === qc[0];
    }
  }
  if (bestSpan) {
    return FUZZY_SCORE.subsequence + (startsWord ? 5 : 0) + Math.round((qc.length / bestSpan) * 4);
  }

  // A typo, never for a query with a digit in it.
  const limit = /\d/.test(qc) ? 0 : allowedTypos(qc.length);
  if (limit > 0) {
    let best = boundedDistance(qc, tc, limit, false);
    for (let w = 1; w < t.words.length && best > 0; w++) {
      best = Math.min(best, boundedDistance(qc, t.words.slice(w).join(''), limit, false));
    }
    if (best <= limit) return FUZZY_SCORE.typo + (limit - best) * 5;
    // One typo in the middle of a single long word ("hammr" inside
    // "mikehammermoving"), for 5+ characters only.
    if (qc.length >= 5 && t.words.some((w) => boundedDistance(qc, w, 1, true) <= 1)) {
      return FUZZY_SCORE.typo - 5;
    }
  }
  return 0;
}

function bestOver(qc: string, fields: readonly Normalized[], strict: boolean): number {
  let best = 0;
  for (const f of fields) {
    const s = scoreNormalized(qc, f, strict);
    if (s > best) best = s;
    if (best === FUZZY_SCORE.exact) break;
  }
  return best;
}

/**
 * Best score of `query` over the item's separate fields. 0 means no match.
 *
 * Monotonic: the item only matches if it also matches every shorter start of
 * the query, so adding a letter can never add a row.
 */
export function fuzzyScore(
  query: string,
  texts: ReadonlyArray<string | null | undefined>,
  { strict = false }: FuzzyOptions = {},
): number {
  const qc = normalizeForSearch(query).compact;
  if (!qc) return 0;
  const fields = texts.map((t) => normalizeForSearch(t)).filter((f) => f.compact);
  const score = bestOver(qc, fields, strict);
  if (score === 0) return 0;
  for (let len = qc.length - 1; len >= 1; len--) {
    if (bestOver(qc.slice(0, len), fields, strict) === 0) return 0;
  }
  return score;
}

/** Score one query against one text (see fuzzyScore). */
export function fuzzyScoreOne(
  query: string,
  text: string | null | undefined,
  options: FuzzyOptions = {},
): number {
  return fuzzyScore(query, [text], options);
}

export function fuzzyMatches(
  query: string,
  texts: string | ReadonlyArray<string | null | undefined> | null | undefined,
  options: FuzzyOptions = {},
): boolean {
  if (!normalizeForSearch(query).compact) return true;
  const list = typeof texts === 'string' || texts == null ? [texts] : texts;
  return fuzzyScore(query, list, options) > 0;
}

export interface FuzzyFilterOptions extends FuzzyOptions {
  /** Sort best match first (stable on ties). Off when the user chose a sort. */
  sort?: boolean;
}

/**
 * Items matching the query. `getTexts` returns the item's fields SEPARATELY
 * (never one joined string). An empty query returns every item untouched, in
 * its original order. With `sort` (the default), results are ordered best
 * match first; ties keep their original order.
 */
export function fuzzyFilter<T>(
  items: readonly T[],
  query: string | null | undefined,
  getTexts: (item: T) => ReadonlyArray<string | null | undefined>,
  { sort = true, strict = false }: FuzzyFilterOptions = {},
): T[] {
  if (!normalizeForSearch(query).compact) return [...items];
  const scored: Array<{ item: T; score: number; index: number }> = [];
  items.forEach((item, index) => {
    const score = fuzzyScore(query ?? '', getTexts(item), { strict });
    if (score > 0) scored.push({ item, score, index });
  });
  if (sort) scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.map((s) => s.item);
}

/**
 * cmdk's `filter` prop: (value, search, keywords) => a rank between 0 and 1,
 * 0 hiding the item. cmdk sorts by it, so the best match comes first. When an
 * item passes `keywords` (its separate fields), those are scored one by one
 * instead of the run-together `value`.
 */
export function cmdkFuzzyFilter(value: string, search: string, keywords?: string[]): number {
  if (!normalizeForSearch(search).compact) return 1;
  const fields = keywords && keywords.length > 0 ? keywords : [value];
  return fuzzyScore(search, fields) / FUZZY_SCORE.exact;
}
